import React, { useEffect, useState } from "react";
import BunkeyApi from "../../api/BunkeyAPI";

const formatDate = value => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}/${month} · ${hours}:${minutes}`;
};

// dueDate se guarda como la medianoche UTC del día elegido. Leído en hora
// local (Chile, UTC-3/-4) caía en el día anterior: un vencimiento del 30/09
// aparecía como 29/09, distinto de lo que muestra la info del archivo.
const formatDueDate = value => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getUTCDate()).padStart(2, "0")}/${String(date.getUTCMonth() + 1).padStart(2, "0")}/${date.getUTCFullYear()}`;
};

const formatValue = (snapshot, fallback, intl) => {
  if (!snapshot) return fallback || "—";
  const status = snapshot.rightsStatus || snapshot.status;
  const dueDate = snapshot.dueDate;
  if (!status && !dueDate) return fallback || "—";

  // La bitácora muestra lo que se asignó en ese cambio, no el estado de hoy.
  // Antes "enabled" se rotulaba Vigente/Vencido según la fecha actual, y un
  // cambio del 16/09 que dejó derechos hasta el 30/09 aparecía "Vencido".
  let label = status || "—";
  if (status === "unassigned") {
    label = intl.formatMessage({ id: "rights_state_unassigned" });
  } else if (status === "not_required") {
    label = intl.formatMessage({ id: "rights_not_required" });
  } else if (status === "enabled") {
    label = intl.formatMessage({
      id: dueDate ? "rights_enabled_due" : "rights_enabled_no_due"
    });
  }

  return `${label}${dueDate ? ` · ${formatDueDate(dueDate)}` : ""}`;
};

// Ajustes que no cambian el rótulo del estado (Ricardo, 2026-10-07): adjuntar
// el contrato o mover la fecha de inicio se leían "Habilitado → Habilitado".
const sameInstant = (a, b) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return new Date(a).getTime() === new Date(b).getTime();
};

const contractChange = entry => {
  const before = entry.previous && entry.previous.licenseFile;
  const after = entry.next && entry.next.licenseFile;
  // Las entradas anteriores a que la bitácora guardara el contrato no traen
  // el campo: no se sabe si cambió.
  if (before === undefined || after === undefined || before === after) return null;
  if (!before) return "rights_history_contract_added";
  if (!after) return "rights_history_contract_removed";
  return "rights_history_contract_replaced";
};

const describeChange = (entry, intl) => {
  const was = formatValue(entry.previous, intl.formatMessage({ id: "rights_history_no_previous" }), intl);
  const now = formatValue(entry.next, intl.formatMessage({ id: "rights_history_no_next" }), intl);
  const prevStart = entry.previous && entry.previous.createdDate;
  const nextStart = entry.next && entry.next.createdDate;
  const startChanged =
    entry.next && entry.next.rightsStatus === "enabled" && !sameInstant(prevStart, nextStart);
  const stateChanged = was !== now;
  return {
    state: stateChanged ? { was, now } : null,
    // El inicio sólo se nombra si es lo único que movió el período: al pasar
    // de Pendiente a Habilitado es parte del cambio y no aporta.
    start: startChanged && !stateChanged
      ? { was: formatDueDate(prevStart) || "—", now: formatDueDate(nextStart) || "—" }
      : null,
    contract: contractChange(entry)
  };
};

const formatOperation = (entry, intl) => {
  const operation = entry.action || entry.operation;
  if (operation === "single") {
    return intl.formatMessage({ id: "rights_history_operation_single" });
  }
  if (operation === "batch" || operation === "bulk" || operation === "lote") {
    return intl.formatMessage({ id: "rights_history_operation_batch" });
  }
  return operation || intl.formatMessage({ id: "rights_history_change" });
};

function RightsHistory({ itemSelected, workclient, user, modules, keyCollection, intl }) {
  const [state, setState] = useState({ loading: false, error: false, entries: [] });
  const isSharedPage = keyCollection === "assetsPublicShared";
  const useRightsActive = Boolean(
    modules && modules.some(module => module.name === "useRights" && module.activate)
  );

  useEffect(() => {
    if (isSharedPage || !user || !itemSelected || !itemSelected._id || !useRightsActive) {
      setState({ loading: false, error: false, entries: [] });
      return undefined;
    }

    let cancelled = false;
    setState({ loading: true, error: false, entries: [] });

    BunkeyApi({
      url: `/clients/${workclient}/rights/assets/${itemSelected._id}/log?userid=${user._id}&page=1&limit=20`,
      method: "GET"
    })
      .then(resp => {
        if (!cancelled) {
          setState({
            loading: false,
            error: false,
            entries: Array.isArray(resp.data) ? resp.data : []
          });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, error: true, entries: [] });
      });

    return () => {
      cancelled = true;
    };
    // rightsState y las fechas cambian cuando se guardan derechos en el mismo
    // panel (updateAssetRightsInList): así el registro nuevo aparece sin
    // tener que volver a abrir el archivo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    itemSelected && itemSelected._id,
    itemSelected && itemSelected.rightsState,
    itemSelected && itemSelected.metadata && itemSelected.metadata.dueDate,
    itemSelected && itemSelected.metadata && itemSelected.metadata.rightsStatus,
    // Cambia en cada guardado, aunque el estado y las fechas queden iguales
    // (por ejemplo, si sólo se adjuntó el contrato).
    itemSelected && itemSelected.metadata && itemSelected.metadata.rightsAssignedAt,
    workclient,
    user && user._id,
    isSharedPage,
    useRightsActive
  ]);

  if (isSharedPage || !user || !useRightsActive) return null;

  // Una entrada donde nada cambió no dice nada: desde ahora el backend ya no
  // las escribe, y las que quedaron de antes se ocultan.
  const entries = state.entries
    .map(entry => ({ entry, change: describeChange(entry, intl) }))
    .filter(({ change }) => change.state || change.start || change.contract);

  return (
    <div className="rights-detail-history rights-detail-history--left-column css-color-bunkers">
      <div className="rights-detail-history__title">
        {intl.formatMessage({ id: "rights_history_title" })}
        {/* "Bitácora derechos de uso · Dos ajustes registrados", el conteo
            más chico (Ricardo, 2026-10-08). Sin entradas lo dice el cuerpo. */}
        {!state.loading && !state.error && entries.length > 0 && (
          <span className="rights-detail-history__count">
            {` · ${intl.formatMessage(
              { id: "rights_history_count" },
              { count: entries.length }
            )}`}
          </span>
        )}
      </div>
      {state.loading && (
        <div className="rights-detail-history__state">
          {intl.formatMessage({ id: "rights_history_loading" })}
        </div>
      )}
      {!state.loading && state.error && (
        <div className="rights-detail-history__state">
          {intl.formatMessage({ id: "rights_history_error" })}
        </div>
      )}
      {!state.loading && !state.error && entries.length === 0 && (
        <div className="rights-detail-history__state">
          {intl.formatMessage({ id: "rights_history_empty" })}
        </div>
      )}
      {!state.loading && !state.error && entries.map(({ entry, change }, index) => (
        <div className="rights-detail-history__entry" key={entry._id || entry.operationId || index}>
          <div className="rights-detail-history__meta">
            {formatDate(entry.createdAt || entry.createdDate || entry.at)}
          </div>
          <div className="rights-detail-history__what">
            <div className="rights-detail-history__action">
              <b>{entry.username || entry.rightsAssignedByName || "—"}</b>
              {` — ${formatOperation(entry, intl)}`}
              {(entry.affectedCount || entry.count) && ` sobre ${entry.affectedCount || entry.count} piezas`}
            </div>
            {change.state && (
              <div className="rights-detail-history__change">
                <span className="rights-detail-history__was">{change.state.was}</span>
                <span aria-hidden="true"> → </span>
                <span className="rights-detail-history__now">{change.state.now}</span>
              </div>
            )}
            {change.start && (
              <div className="rights-detail-history__change">
                {intl.formatMessage({ id: "rights_history_start" })}
                <span className="rights-detail-history__was">{change.start.was}</span>
                <span aria-hidden="true"> → </span>
                <span className="rights-detail-history__now">{change.start.now}</span>
              </div>
            )}
            {change.contract && (
              <div className="rights-detail-history__change">
                <span className="rights-detail-history__now">
                  {intl.formatMessage({ id: change.contract })}
                </span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default RightsHistory;
